import { AssignmentType } from '@app/features/contracts/embauche-dialog/embauche-tabs-form/assignment-form/assignment-type.model';
import { ContractStatus, SchedulePart } from '@app/features/contracts/contract.model';
import { SignatureRequest } from '@app/features/contracts/signature/signature-request.model';
import { ValidationRequest } from '@app/features/contracts/validation/validation-request.model';
import { DocumentTag } from '@app/features/documents/document.model';
import { Residency } from '@app/features/residences/residency.model';

/** Les motifs d'un avenant, que l'API déduit de ce qu'il change (RG-avenant-motifs). */
export const AMENDMENT_REASONS = [
  'WORKING_TIME_INCREASE',
  'WORKING_TIME_DECREASE',
  'SCHEDULE_CHANGE',
  'ASSIGNMENT_CHANGE',
] as const;
export type AmendmentReasonCode = (typeof AMENDMENT_REASONS)[number];

export interface AmendmentReason {
  code: AmendmentReasonCode;
  label: string;
}

/** L'état d'un avenant : les échelons du contrat, le contrat restant signé (décision 04). */
export type AmendmentStatus = ContractStatus;

/** Ce qui a abandonné un avenant : son auteur, son contrat annulé ou supprimé, ou Yousign. */
export type AmendmentAbandonCause =
  'USER' | 'ABANDONED_IN_SIGNING' | 'CONTRACT_CANCELED' | 'CONTRACT_DELETED' | 'SIGNATURE_CANCELED';

export interface AmendmentDocument {
  id: string;
  tag: DocumentTag;
  name: string;
  attachedAt: string;
}

/**
 * Ce que les gestes d'un avenant en connaissent : l'avenant entier, dans sa fenêtre, ou ce que sa
 * ligne en montre dans la liste des contrats.
 */
export interface AmendmentActionsSubject {
  id: string;
  number?: number | null;
  status: AmendmentStatus;
  fileName?: string | null;
  validationRequests?: ValidationRequest[] | null;
  signatureRequests: SignatureRequest[];
}

export interface Amendment {
  id: string;
  contractId: string;
  /** Attribué à l'acceptation du DRH (RG-avenant-numerotation). */
  number: number | null;
  status: AmendmentStatus;
  effectiveDate: string;
  /** La fin d'un avenant temporaire ; vide pour un avenant à durée indéterminée. */
  endDate: string | null;
  schedule: SchedulePart;
  weeklyWorkingHours: number;
  monthlyWorkingHours: number;
  assignmentType: AssignmentType;
  residencies: Residency[];
  reasons: AmendmentReason[];
  /**
   * Le nom de téléchargement, sans extension (RG-avenant-nom-de-fichier) : avant l'acceptation du
   * DRH, sous le numéro que l'avenant recevra.
   */
  fileName: string | null;
  /**
   * Les conditions que l'avenant remplace : celles en vigueur à sa date d'effet, sans lui — l'avant
   * de la vue des modifications (RG-avenant-consultation).
   */
  previousConditions: WorkingConditions;
  createdAt: string;
  createdBy: string;
  /** Quand, par qui et pourquoi l'avenant a été abandonné ; vides tant qu'il ne l'est pas. */
  abandonedAt?: string | null;
  abandonedBy?: string | null;
  abandonCause?: AmendmentAbandonCause | null;
  /** Le motif d'un abandon en signature, par le directeur RH (RG-avenant-flot-de-validation). */
  abandonComment?: string | null;
  /** Quand l'avenant a été signé, et qui en a déposé l'exemplaire ; aucun auteur pour Yousign. */
  signedAt?: string | null;
  signedBy?: string | null;
  signatureSendFailedAt: string | null;
  signatureSendFailedBy: string | null;
  signatureSendFailureReason: string | null;
  /** La coche « Édité » de l'avenant (RG-avenant-coche-edite). */
  adpEditedAt?: string | null;
  adpEditedBy?: string | null;
  validationRequests: ValidationRequest[];
  signatureRequests: SignatureRequest[];
  documents: AmendmentDocument[];
}

/**
 * Les nouvelles conditions d'un avenant, sa date d'effet, sa date de fin s'il est temporaire, et
 * les motifs que l'utilisateur coche.
 */
export interface AmendmentPayload {
  effectiveDate: string;
  endDate: string | null;
  reasons: AmendmentReasonCode[];
  schedule: SchedulePart;
  assignmentType: AssignmentType;
  residencies: Residency[];
}

/** Les conditions de travail d'un contrat en vigueur à une date. */
export interface WorkingConditions {
  schedule: SchedulePart;
  assignmentType: AssignmentType;
  residencies: Residency[];
  weeklyWorkingHours: number;
}

/** Les motifs tels que la carte les fait cocher (A, B, C, D). */
export const DECLARABLE_REASONS: { code: AmendmentReasonCode; label: string }[] = [
  { code: 'WORKING_TIME_INCREASE', label: 'Augmentation du temps de travail' },
  { code: 'WORKING_TIME_DECREASE', label: 'Diminution du temps de travail' },
  { code: 'ASSIGNMENT_CHANGE', label: "Changement d'affectation" },
  { code: 'SCHEDULE_CHANGE', label: "Changement d'horaire" },
];

/** Si l'action « Établir un avenant » se propose, et sinon pourquoi. */
export interface AmendmentEligibility {
  allowed: boolean;
  refusal: string | null;
}

/** Les motifs déduits pendant la saisie, et ce qui empêcherait l'enregistrement. */
export interface AmendmentReasons {
  reasons: AmendmentReason[];
  weeklyWorkingHoursInForce: number;
  weeklyWorkingHours: number;
  /** Le nom que portera l'avenant, sous le numéro qu'il recevra (RG-avenant-nom-de-fichier). */
  fileName: string | null;
  refusal: string | null;
}

/** Un avenant ni signé ni abandonné bloque l'établissement d'un autre (RG-avenant-un-seul-en-cours). */
export function isAmendmentInProgress(amendment: Pick<Amendment, 'status'>): boolean {
  return amendment.status !== 'SIGNED' && amendment.status !== 'CANCELED';
}
